<script setup lang="ts">
import IconDropdownClose from '@patsnap-ui/icon/assets/solid/DropdownClose.svg';
import IconDropdownOpen from '@patsnap-ui/icon/assets/solid/DropdownOpen.svg';
import IconTick from '@patsnap-ui/icon/assets/solid/tick.svg';
import SvgIcon from '@telecom/shared/components/SvgIcon.vue';
import { ref, computed, onMounted } from 'vue';
import { ElPopover } from 'element-plus';
import { useClaimFeatures } from './composables/use-claim-features';
import { useI18n } from 'vue-i18n';
import type { SelectedClaimFeature } from './types';

const props = defineProps<{
    modelValue?: SelectedClaimFeature;
}>();

const emits = defineEmits<{
    (e: 'update:modelValue', value: SelectedClaimFeature): void;
}>();

const { t } = useI18n();

const isShowPopover = ref(false);

// 权利要求及特征词
const { claimFeatures } = useClaimFeatures();

// 当前选中的分组
const currentActiveGroup = ref('');

function toggleCurrentActiveGroup(target: string) {
    currentActiveGroup.value = target;
}

function setDefaultCurrentActiveGroup() {
    const matchedItem = claimFeatures.value.find(item => item.num === props.modelValue?.num);
    currentActiveGroup.value = matchedItem?.num || claimFeatures.value[0]?.num || '';
}

// 当前分组对应的值
const currentValues = computed(() => {
    return claimFeatures.value.find(item => item.num === currentActiveGroup.value)?.features || [];
});

const selectedValue = computed({
    get: () => props.modelValue || { num: '', feature: '' },
    set: (value) => emits('update:modelValue', value),
});

function handleSelect(feature: string) {
    selectedValue.value = {
        num: currentActiveGroup.value,
        feature,
    };
    isShowPopover.value = false;
}

onMounted(() => {
    setDefaultCurrentActiveGroup();
});
</script>

<template>
    <ElPopover
        v-model:visible="isShowPopover"
        :trigger="'click'"
        :showArrow="false"
        :placement="'bottom-start'"
        :hideAfter="0"
        :width="400"
        :transition="'none'"
        popperClass="embodiment-feature-selector__popover-wrapper"
        :popperOptions="{ modifiers: [{ name: 'offset', options: { offset: [0, 4] } }] }"
    >
        <!-- 触发按钮 -->
        <template #reference>
            <div
                :class="{ '!border-green-500': isShowPopover }"
                class="embodiment-feature-selector w-full py-1 px-3 pr-1 rounded bg-green-75 border border-green-75 flex justify-between items-center overflow-hidden cursor-pointer"
            >
                <!-- 已选择的特征显示区域 -->
                <p class="overflow-hidden text-ellipsis whitespace-nowrap flex-1">
                    <template v-if="selectedValue.num && selectedValue.feature">
                        <!-- 权利要求编号 -->
                        <span class="text-gray-450">
                            {{ t('aiSpecification.claimNum', { num: selectedValue.num }) }}
                        </span>
                        <!-- 选中的特征 -->
                        <span class="ml-1 text-black">
                            {{ selectedValue.feature }}
                        </span>
                    </template>
                    <!-- 未选择时的占位文本 -->
                    <span v-else class="text-gray-90">
                        {{ t('aiSpecification.embodiment.replacedFeaturePlaceholder') }}
                    </span>
                </p>
                <!-- 展开/收起图标 -->
                <SvgIcon
                    class="th-btn-icon h-6 w-6 fill-gray-450 flex-shrink-0"
                    :icon="isShowPopover ? IconDropdownClose : IconDropdownOpen"
                />
            </div>
        </template>

        <!-- 弹出层内容 -->
        <template #default>
            <div class="embodiment-feature-selector__popover h-[200px] flex">
                <!-- 左侧权利要求分组列表 -->
                <div class="embodiment-feature-selector__popover__group w-[120px] border-r border-gray-40 pr-2 mr-2 flex-shrink-0 overflow-auto">
                    <!-- 权利要求分组项 -->
                    <div
                        v-for="item in claimFeatures"
                        :key="item.num"
                        :class="{ '!bg-green-75': currentActiveGroup === item.num }"
                        class="embodiment-feature-selector__popover__group-item p-1 cursor-pointer rounded mb-1 hover:bg-gray-20"
                        @click="toggleCurrentActiveGroup(item.num)"
                    >
                        <span class="leading-6">{{ t('aiSpecification.claimNum', { num: item.num }) }}</span>
                    </div>
                </div>

                <!-- 右侧特征选择区域 -->
                <div class="embodiment-feature-selector__popover__selector flex flex-col flex-1">
                    <!-- 标题 -->
                    <div class="embodiment-feature-selector__popover__selector-title flex-shrink-0 text-gray-90 text-xs mb-1">
                        {{ t('aiSpecification.embodiment.featureInClaim', { num: currentActiveGroup }) }}
                    </div>
                    <!-- 特征列表 -->
                    <div class="embodiment-feature-selector__popover__selector-content flex-1 overflow-auto">
                        <!-- 特征项 -->
                        <div
                            v-for="feature in currentValues"
                            :key="feature"
                            class="embodiment-feature-selector__popover__selector-item p-1 hover:bg-gray-20 cursor-pointer rounded mb-1 flex items-start justify-between"
                            @click="handleSelect(feature)"
                        >
                            <!-- 特征文本 -->
                            <span class="leading-6">{{ feature }}</span>
                            <!-- 选中状态图标 -->
                            <SvgIcon
                                :icon="IconTick"
                                class="th-btn-icon w-6 h-6 fill-green-500 flex-shrink-0 ml-2 invisible"
                                :class="{ '!visible': (selectedValue.feature == feature) && (selectedValue.num === currentActiveGroup) }"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </ElPopover>
</template>
